import { type Tenant, useTenantStore } from '@/stores/tenantStore'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { fetchClient } from '../typedClient'
import type { InferRequest } from '../typedClient'
import { System } from '../types'

const crossroadsApi = {
  getSystems: async (tenantName: string): Promise<System[]> => {
    const { data, error } = await fetchClient.GET(
      '/crossroads/network/systems/',
      { params: { header: { 'X-Tenant-Name': tenantName } } },
    )
    if (error) {
      throw new Error(`Failed to fetch systems: ${JSON.stringify(error)}`)
    }
    return data as unknown as System[]
  },
  createSystem: async (system: Omit<System, 'id'>): Promise<System> => {
    const { data, error } = await fetchClient.POST(
      '/crossroads/network/systems/',
      {
        params: { header: { 'X-Tenant-Name': 'Gravitate' } },
        body: system as never,
      },
    )
    if (error) {
      throw new Error(`Failed to create system: ${JSON.stringify(error)}`)
    }
    return data as unknown as System
  },
  updateSystem: async (system: System): Promise<System> => {
    const systemId = system._id || system.id
    const body: { description?: string; type?: string } = {}
    if (system.description !== undefined) {
      body.description = system.description
    }
    if (system.type !== undefined) {
      body.type = system.type
    }
    const { data, error } = await fetchClient.PUT(
      // @ts-expect-error TODO: Add /crossroads/network/systems/{system_id} PUT to OpenAPI spec
      '/crossroads/network/systems/{system_id}',
      {
        params: {
          path: { system_id: systemId },
          header: { 'X-Tenant-Name': 'Gravitate' },
        },
        body,
      },
    )
    if (error) {
      throw new Error(`Failed to update system: ${JSON.stringify(error)}`)
    }
    return data as unknown as System
  },
  deleteSystem: async (id: string): Promise<void> => {
    const { error } = await fetchClient.DELETE(
      // @ts-expect-error TODO: Add /crossroads/network/systems/{system_id} DELETE to OpenAPI spec
      '/crossroads/network/systems/{system_id}',
      {
        params: {
          path: { system_id: id },
          header: { 'X-Tenant-Name': 'Gravitate' },
        },
      },
    )
    if (error) {
      throw new Error(`Failed to delete system: ${JSON.stringify(error)}`)
    }
  },
  getSupportedSystemTypes: async (): Promise<string[]> => {
    const { data, error } = await fetchClient.GET(
      '/crossroads/network/systems/supported_systems',
      { params: { header: { 'X-Tenant-Name': 'Gravitate' } } },
    )
    if (error) {
      throw new Error(
        `Failed to fetch supported system types: ${JSON.stringify(error)}`,
      )
    }
    return data as unknown as string[]
  },
  getTenants: async (): Promise<Tenant[]> => {
    const { data, error } = await fetchClient.POST('/meta/tenant/all')
    if (error) {
      throw new Error(`Failed to fetch tenants: ${JSON.stringify(error)}`)
    }
    return data as unknown as Tenant[]
  },
  createTenant: async (
    body: InferRequest<'/meta/tenant/create'>,
  ): Promise<Tenant> => {
    const { data, error } = await fetchClient.POST('/meta/tenant/create', {
      params: { header: { 'X-Tenant-Name': 'Gravitate' } },
      body,
    })
    if (error) {
      throw new Error(`Failed to create tenant: ${JSON.stringify(error)}`)
    }
    return data as unknown as Tenant
  },
  updateTenant: async ({
    tenant_id,
    body,
  }: {
    tenant_id: string
    body: InferRequest<'/meta/tenant/update'>
  }): Promise<Tenant> => {
    const { data, error } = await fetchClient.POST('/meta/tenant/update', {
      params: {
        query: { tenant_id },
        header: { 'X-Tenant-Name': 'Gravitate' },
      },
      body,
    })
    if (error) {
      throw new Error(`Failed to update tenant: ${JSON.stringify(error)}`)
    }
    return data as unknown as Tenant
  },
}

export const useSystemsList = () => {
  const { selectedTenant } = useTenantStore()
  const tenantName = selectedTenant?.name ?? ''

  return useQuery({
    queryKey: ['systems', tenantName],
    queryFn: () => crossroadsApi.getSystems(tenantName),
    enabled: !!tenantName,
  })
}

export const useCreateSystem = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: crossroadsApi.createSystem,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['systems'] })
    },
  })
}

export const useUpdateSystem = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: crossroadsApi.updateSystem,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['systems'] })
    },
  })
}

export const useDeleteSystem = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: crossroadsApi.deleteSystem,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['systems'] })
    },
  })
}

export const useSupportedSystemTypes = () => {
  return useQuery({
    queryKey: ['supportedSystemTypes'],
    queryFn: crossroadsApi.getSupportedSystemTypes,
  })
}

export const useTenantsList = () => {
  return useQuery({
    queryKey: ['tenants'],
    queryFn: crossroadsApi.getTenants,
  })
}

export const useCreateTenant = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: crossroadsApi.createTenant,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenants'] })
    },
  })
}

export const useUpdateTenant = () => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: crossroadsApi.updateTenant,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenants'] })
    },
  })
}
